import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, GizmoHelper, GizmoViewport, Html } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Play, Pause, RotateCcw, Eye, EyeOff, Box } from 'lucide-react';
import { IDS } from '@/constants/testIds/wizard';

// Convert a toolpath segment to sample points (for arcs)
function segmentToPoints(seg, steps = 24) {
  const s = new THREE.Vector3(seg.start[0], seg.start[1], seg.start[2]);
  const e = new THREE.Vector3(seg.end[0], seg.end[1], seg.end[2]);
  if (seg.kind === 'rapid' || seg.kind === 'linear') return [s, e];
  if (!seg.center) return [s, e];
  const c = new THREE.Vector3(seg.center[0], seg.center[1], seg.center[2]);
  const v0 = new THREE.Vector3().subVectors(s, c);
  const v1 = new THREE.Vector3().subVectors(e, c);
  const r = v0.length();
  let a0 = Math.atan2(v0.y, v0.x);
  let a1 = Math.atan2(v1.y, v1.x);
  const cw = seg.kind === 'arc_cw';
  // Ensure sweep direction
  let sweep = a1 - a0;
  if (cw) {
    if (sweep > 0) sweep -= 2 * Math.PI;
  } else {
    if (sweep < 0) sweep += 2 * Math.PI;
  }
  // Handle full-circle case (start == end)
  if (Math.abs(sweep) < 1e-6 && s.distanceTo(e) < 1e-6) sweep = cw ? -2 * Math.PI : 2 * Math.PI;
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = a0 + sweep * t;
    const z = s.z + (e.z - s.z) * t;
    pts.push(new THREE.Vector3(c.x + r * Math.cos(a), c.y + r * Math.sin(a), z));
  }
  return pts;
}

function buildPathGeometry(segments) {
  const cutPts = [];
  const rapidPts = [];
  const cumulative = []; // cumulative points for the tool animation
  let totalPoints = [];
  for (const seg of segments) {
    const pts = segmentToPoints(seg);
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      if (seg.kind === 'rapid') {
        rapidPts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      } else {
        cutPts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
    for (const p of pts) totalPoints.push(p);
  }
  return {
    cutPositions: new Float32Array(cutPts),
    rapidPositions: new Float32Array(rapidPts),
    animPoints: totalPoints,
  };
}

function ToolpathLines({ cutPositions, rapidPositions, showRapids }) {
  const cutGeom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    if (cutPositions.length) g.setAttribute('position', new THREE.BufferAttribute(cutPositions, 3));
    return g;
  }, [cutPositions]);
  const rapidGeom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    if (rapidPositions.length) g.setAttribute('position', new THREE.BufferAttribute(rapidPositions, 3));
    return g;
  }, [rapidPositions]);
  return (
    <>
      {cutPositions.length > 0 && (
        <lineSegments geometry={cutGeom}>
          <lineBasicMaterial color="#00FF41" linewidth={1} />
        </lineSegments>
      )}
      {showRapids && rapidPositions.length > 0 && (
        <lineSegments geometry={rapidGeom}>
          <lineBasicMaterial color="#7F8B96" transparent opacity={0.4} />
        </lineSegments>
      )}
    </>
  );
}

function AnimatedTool({ animPoints, progress, toolDiameter = 6 }) {
  const ref = useRef();
  const pos = useMemo(() => {
    if (!animPoints.length) return new THREE.Vector3(0, 0, 10);
    const idx = Math.min(animPoints.length - 1, Math.floor(progress * (animPoints.length - 1)));
    return animPoints[idx];
  }, [animPoints, progress]);
  useEffect(() => {
    if (ref.current) ref.current.position.set(pos.x, pos.y, pos.z + 10);
  }, [pos]);
  const r = Math.max(0.5, toolDiameter / 2);
  return (
    <group ref={ref}>
      {/* Tool shank */}
      <mesh position={[0, 0, 12]}>
        <cylinderGeometry args={[r * 1.2, r * 1.2, 20, 24]} />
        <meshStandardMaterial color="#C7D0D9" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Cutter */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[r, r, 4, 24]} />
        <meshStandardMaterial color="#B0B8C0" metalness={0.9} roughness={0.2} />
      </mesh>
      {/* Tool tip indicator */}
      <mesh position={[0, 0, -2.05]}>
        <coneGeometry args={[r * 0.4, 0.4, 12]} />
        <meshStandardMaterial color="#00FF41" emissive="#00FF41" emissiveIntensity={0.6} />
      </mesh>
    </group>
  );
}

function Stock({ bounds, visible, toolDiameter = 6 }) {
  if (!visible || !bounds) return null;
  const [minX, minY, maxX, maxY] = bounds;
  const w = Math.max(10, maxX - minX + toolDiameter * 2);
  const h = Math.max(10, maxY - minY + toolDiameter * 2);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return (
    <mesh position={[cx, cy, -2.5]} rotation={[0, 0, 0]}>
      <boxGeometry args={[w, h, 5]} />
      <meshStandardMaterial color="#2A343B" transparent opacity={0.35} />
    </mesh>
  );
}

function Scene({ toolpath, playing, progress, setProgress, showRapids, showStock, toolDiameter, view }) {
  const geo = useMemo(() => buildPathGeometry(toolpath || []), [toolpath]);
  const bounds = useMemo(() => {
    if (!geo.animPoints.length) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of geo.animPoints) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return [minX, minY, maxX, maxY];
  }, [geo]);

  useFrame((state, delta) => {
    if (playing && geo.animPoints.length > 1) {
      const speed = 0.05; // fraction/sec
      setProgress(prev => {
        const n = prev + delta * speed;
        return n >= 1 ? 1 : n;
      });
    }
  });

  const { camera } = useThree();
  useEffect(() => {
    if (!bounds) return;
    const [minX, minY, maxX, maxY] = bounds;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const size = Math.max(50, Math.max(maxX - minX, maxY - minY));
    if (view === 'top') {
      camera.position.set(cx, cy, size * 1.5);
      camera.up.set(0, 1, 0);
      camera.lookAt(cx, cy, 0);
    } else if (view === 'front') {
      camera.position.set(cx, -size * 1.5, size * 0.4);
      camera.up.set(0, 0, 1);
      camera.lookAt(cx, cy, 0);
    } else {
      // iso
      camera.position.set(cx + size, cy - size, size * 0.8);
      camera.up.set(0, 0, 1);
      camera.lookAt(cx, cy, 0);
    }
  }, [view, bounds, camera]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[50, 50, 100]} intensity={0.9} />
      <directionalLight position={[-50, -50, 50]} intensity={0.3} color="#00FF41" />
      <Grid args={[400, 400]} cellSize={5} cellColor="#1C2A31" sectionSize={20} sectionColor="#263238" position={[0, 0, -2.6]} fadeDistance={300} infiniteGrid rotation={[Math.PI / 2, 0, 0]} />
      <Stock bounds={bounds} visible={showStock} toolDiameter={toolDiameter} />
      <ToolpathLines cutPositions={geo.cutPositions} rapidPositions={geo.rapidPositions} showRapids={showRapids} />
      <AnimatedTool animPoints={geo.animPoints} progress={progress} toolDiameter={toolDiameter} />
      {/* Origin marker */}
      <group>
        <mesh position={[2.5, 0, 0]}><boxGeometry args={[5, 0.15, 0.15]} /><meshBasicMaterial color="#FF4D4D" /></mesh>
        <mesh position={[0, 2.5, 0]}><boxGeometry args={[0.15, 5, 0.15]} /><meshBasicMaterial color="#00FF41" /></mesh>
        <mesh position={[0, 0, 2.5]}><boxGeometry args={[0.15, 0.15, 5]} /><meshBasicMaterial color="#4DB6FF" /></mesh>
      </group>
    </>
  );
}

export const Viewport3D = ({ toolpath, params, op }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showRapids, setShowRapids] = useState(true);
  const [showStock, setShowStock] = useState(true);
  const [view, setView] = useState('iso');

  const toolDiameter = params?.tool_diameter || 6;

  return (
    <div className="flex-1 min-h-0 flex flex-col relative" data-testid={IDS.viewport.viewport}>
      {/* Header */}
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <Box className="h-4 w-4 text-primary" />
          <span className="panel-title">3D nezet</span>
          <span className="text-[10px] text-muted-foreground uppercase">• {(toolpath || []).length} szegmens</span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="h-7 w-32 text-xs" data-testid={IDS.viewport.viewPreset}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="iso">Izometrikus</SelectItem>
              <SelectItem value="top">Felulnezet</SelectItem>
              <SelectItem value="front">Elolnezet</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 text-xs">
            <Switch checked={showRapids} onCheckedChange={setShowRapids} />
            <span className="text-muted-foreground">Rapid</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Switch checked={showStock} onCheckedChange={setShowStock} data-testid={IDS.viewport.toggleStock} />
            <span className="text-muted-foreground">Munkadarab</span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative bg-[#0A0C0D] grid-overlay overflow-hidden">
        <Canvas
          camera={{ position: [80, -80, 80], up: [0, 0, 1], fov: 45 }}
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl }) => { gl.setClearColor(new THREE.Color('#0A0C0D')); }}
        >
          <Scene
            toolpath={toolpath}
            playing={playing}
            progress={progress}
            setProgress={setProgress}
            showRapids={showRapids}
            showStock={showStock}
            toolDiameter={toolDiameter}
            view={view}
          />
          <OrbitControls enableDamping dampingFactor={0.08} rotateSpeed={0.7} zoomSpeed={0.9} />
          <GizmoHelper alignment="bottom-right" margin={[64, 64]}>
            <GizmoViewport axisColors={['#FF4D4D','#00FF41','#4DB6FF']} labelColor="#0B0D0E" />
          </GizmoHelper>
        </Canvas>

        {/* HUD legend */}
        <div className="absolute top-2 left-2 flex items-center gap-3 px-3 py-1.5 rounded-md bg-black/50 backdrop-blur border border-border/60 text-[10px] tabular">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-[2px] bg-[#00FF41]" />Vagas</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-[2px] bg-[#7F8B96]" />Rapid</span>
          <span className="text-muted-foreground">op: {op}</span>
        </div>

        {/* Playback */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 px-3 py-2 rounded-md bg-black/60 backdrop-blur border border-border/60">
          <Button size="icon" variant="ghost" onClick={() => { if (progress >= 1) setProgress(0); setPlaying(p => !p); }} data-testid={playing ? IDS.viewport.pause : IDS.viewport.play}>
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => { setProgress(0); setPlaying(false); }} data-testid={IDS.viewport.reset}>
            <RotateCcw className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <Slider
              value={[progress * 1000]}
              min={0} max={1000} step={1}
              onValueChange={(v) => { setProgress(v[0] / 1000); setPlaying(false); }}
              data-testid={IDS.viewport.scrubber}
            />
          </div>
          <span className="text-[10px] tabular text-muted-foreground w-14 text-right">{Math.round(progress * 100)}%</span>
        </div>

        {(toolpath || []).length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center opacity-70">
              <div className="text-4xl mb-2">⚙️</div>
              <p className="text-sm text-muted-foreground">Nincs generalt szerszampalya</p>
              <p className="text-xs text-muted-foreground/70">Toltsd ki a parametereket es kattints a “G-kod generalasa” gombra</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Viewport3D;
